import Grid from '@mui/material/Grid';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { DebounceField } from '@openx/components-core/lib/DebounceField/DebounceField.tsx';
import { InvalidItems } from '@openx/components-targeting/Targeting/shared/BulkAdd/InvalidItems/InvalidItems.tsx';
import { type ChangeEvent, type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { useValidateLocations } from '../../../hooks/useValidateLocations.tsx';
import { getBulkPlaceholders } from './getBulkPlaceholders.ts';

type BulkAddProps = {
  zipSegments: FormSegment[];
  setSegments: Dispatch<SetStateAction<FormSegment[]>>;
};

const SEPARATOR = /\n|,|;/;

export const LocationBulkAdd = ({ zipSegments, setSegments }: BulkAddProps) => {
  const { t } = useTranslation();

  const placeHolder = useMemo(() => getBulkPlaceholders(t), [t]);

  const [search, setSearch] = useState<string[]>([]);

  const onTextareaChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value = '' } = event.target;

    const uniqueValues = new Set<string>();

    value.split(SEPARATOR).forEach(item => {
      const trimmed = item.trim();

      if (trimmed) {
        uniqueValues.add(trimmed);
      }
    });

    setSearch(Array.from(uniqueValues));
  }, []);

  const { bulkValidItems, errors, handleValidation, resetValidationState, loading } = useValidateLocations({
    zipSegments,
    search,
    setSearch,
  });

  const onApply = useCallback(() => {
    if (bulkValidItems.length) {
      setSegments((prev: FormSegment[]) => [...prev, ...bulkValidItems]);
      resetValidationState();
      setSearch([]);
    }
  }, [bulkValidItems, resetValidationState, setSegments]);

  return (
    <>
      {!!errors.length && <InvalidItems errors={errors} />}

      <DebounceField
        data-test="zip-codes-bulk"
        multiline
        minRows={10}
        fullWidth
        placeholder={placeHolder.zip}
        onChange={onTextareaChange}
        value={search.join('\n')}
        label={t('Zip Code')}
        helperText={t('Enter up to 20,000 Codes.')}
        disabled={loading}
      />
      <Grid
        container
        sx={{
          pt: 2,
        }}
      >
        <Grid>
          <Button disabled={!search.length} loading={loading} onClick={handleValidation} data-test="validate-codes">
            {t('Validate codes')}
          </Button>
        </Grid>

        <Grid
          sx={{
            pl: 1,
          }}
        >
          <Button onClick={onApply} disabled={!bulkValidItems.length} data-test="apply-codes">
            {t('Apply')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
